import React, { useState } from 'react';
import { useAudio } from '../contexts/AudioContext';
import { useLocation } from 'react-router-dom';
import { Music, Play, Pause, Volume2, VolumeX, ChevronDown, ChevronUp } from 'lucide-react';

const MusicPlayer = () => {
  const location = useLocation();
  const {
    currentMusic,
    isPlaying,
    isPaused,
    volume,
    setVolume,
    togglePlayPause
  } = useAudio();
  
  const [isExpanded, setIsExpanded] = useState(false);

  // Don't show player if no music is assigned
  if (!currentMusic) return null;
  
  // Don't show player on HomePage (landing page)
  if (location.pathname === '/') return null;

  return (
    <div className="fixed top-4 right-4 z-50 lg:top-4 lg:right-4 top-16 right-2">
      {/* Collapsed View */}
      {!isExpanded && (
        <button
          onClick={() => setIsExpanded(true)}
          className="bg-gradient-to-r from-purple-900/95 to-indigo-900/95 backdrop-blur-sm rounded-full shadow-2xl border border-purple-500/30 p-2 hover:scale-110 transition-transform"
        >
          <Music className="w-5 h-5 text-purple-300" />
        </button>
      )}

      {/* Expanded View */}
      {isExpanded && (
        <div className="bg-gradient-to-r from-purple-900/95 to-indigo-900/95 backdrop-blur-sm rounded-lg shadow-2xl border border-purple-500/30 p-2 w-52 lg:w-52 w-44">
          {/* Header with Collapse Button */}
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-1.5">
              <Music className="w-3 h-3 text-purple-300 flex-shrink-0" />
              <h3 className="text-xs font-semibold text-white truncate">Background Music</h3>
            </div>
            <button
              onClick={() => setIsExpanded(false)}
              className="text-purple-300 hover:text-purple-100"
            >
              <ChevronUp className="w-3 h-3" />
            </button>
          </div>

          {/* Now Playing */}
          <div className="mb-1.5">
            <p className="text-xs text-gray-400">Now Playing</p>
            <p className="text-xs font-medium text-white truncate">{currentMusic.title}</p>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-2">
            {/* Play/Pause Button */}
            <button
              onClick={togglePlayPause}
              className="flex items-center justify-center w-8 h-8 lg:w-6 lg:h-6 bg-purple-600 hover:bg-purple-500 rounded-full transition-colors flex-shrink-0"
            >
              {isPlaying ? (
                <Pause className="w-4 h-4 lg:w-3 lg:h-3 text-white" />
              ) : (
                <Play className="w-4 h-4 lg:w-3 lg:h-3 text-white ml-0.5" />
              )}
            </button>

            {/* Volume Control */}
            <div className="flex-1 flex items-center gap-1.5">
              {volume === 0 ? (
                <VolumeX className="w-3 h-3 text-gray-400 flex-shrink-0" />
              ) : (
                <Volume2 className="w-3 h-3 text-gray-400 flex-shrink-0" />
              )}
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={volume}
                onChange={(e) => setVolume(parseFloat(e.target.value))}
                className="flex-1 h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
              />
              <span className="text-xs text-gray-400 w-7 flex-shrink-0">{Math.round(volume * 100)}%</span>
            </div>
          </div>

          {/* Loop Indicator */}
          <div className="mt-1.5 flex items-center justify-center">
            <span className="text-xs text-purple-300 flex items-center gap-1">
              <span className="inline-block w-1 h-1 bg-purple-400 rounded-full animate-pulse"></span>
              Looping
            </span>
          </div>
        </div>
      )}

      <style>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          width: 10px;
          height: 10px;
          background: #a78bfa;
          border-radius: 50%;
          cursor: pointer;
        }

        .slider::-moz-range-thumb {
          width: 10px;
          height: 10px;
          background: #a78bfa;
          border-radius: 50%;
          cursor: pointer;
          border: none;
        }

        .slider::-webkit-slider-thumb:hover {
          background: #c4b5fd;
        }

        .slider::-moz-range-thumb:hover {
          background: #c4b5fd;
        }
      `}</style>
    </div>
  );
};

export default MusicPlayer;
